import getRelativeOffset from './get-relative-offsets';
import selectors from '../../../common/constants/selectors';
import {
  ASPECT_RATIO_CSS_PROPERTY,
  CONTENT_POSITION_X_CSS_PROPERTY,
  CONTENT_POSITION_Y_CSS_PROPERTY,
} from '../constants/media-container';

const DEFAULT_CONTENT_POSITION = [50, 50];

const setAspectRatio = (element, newAspectRatio) => {
  element.style.setProperty(ASPECT_RATIO_CSS_PROPERTY, newAspectRatio);

  // The drag preview writes the aspect ratio as an inline style rather than as the
  // `aspect-ratio` attribute (the attribute is only written once, on commit), so nothing
  // triggers `attributeChangedCallback` and the component would keep rendering the old
  // ratio. Its ResizeObserver is no help either: an aspect-ratio change with a pinned
  // width does resize the box, but a height-only drag can land on the same box it
  // started from. Hence the explicit render. Keep this call when the component moves to
  // computed-first reads — the inline style wins there too, but it still needs the nudge.
  if (element.matches(selectors.MEDIA_CONTAINER)) {
    element.renderMedia();
  }
};

const handleAspectRatioChanges = ({ element, previousAspectRatio, newAspectRatio, bounds }) => {
  const { width, height } = bounds;
  const { contentPosition: elementContentPosition } = element;
  // fallback value for ws-composite-group
  const contentPosition = elementContentPosition || DEFAULT_CONTENT_POSITION;

  if (previousAspectRatio === newAspectRatio) return;

  const { top, bottom } = getRelativeOffset({
    width,
    mediaAspectRatio: previousAspectRatio,
    containerHeight: height,
    position: contentPosition,
  });

  if (top < 0 && bottom > 100) {
    const nextContainerHeight = width / newAspectRatio;
    const [x, y] = contentPosition;

    if (previousAspectRatio >= newAspectRatio) {
      element.style.setProperty(CONTENT_POSITION_X_CSS_PROPERTY, `${x}%`);
      element.style.setProperty(CONTENT_POSITION_Y_CSS_PROPERTY, '50%');
      setAspectRatio(element, newAspectRatio);
      return;
    }

    const { top: nextTopOffset, bottom: nextBottomOffset } = getRelativeOffset({
      width,
      mediaAspectRatio: previousAspectRatio,
      containerHeight: nextContainerHeight,
      position: contentPosition,
    });

    let newVerticalPosition;

    if (nextTopOffset >= 0) {
      newVerticalPosition = Math.round(y - nextTopOffset);
    } else if (nextBottomOffset < 100) {
      newVerticalPosition = Math.round(y + (100 - nextBottomOffset));
    }

    if (Number.isFinite(newVerticalPosition)) {
      element.style.setProperty(CONTENT_POSITION_X_CSS_PROPERTY, `${x}%`);
      element.style.setProperty(CONTENT_POSITION_Y_CSS_PROPERTY, `${newVerticalPosition}%`);
    }
  }

  setAspectRatio(element, newAspectRatio);
};

export default handleAspectRatioChanges;
